import { useMemo, useState } from 'react';

import * as Dfns from 'date-fns/fp';
import { Col, ListGroup, Modal, Row } from 'react-bootstrap';

import {
  formatMoney,
  formatMoneyAsInt,
  formatNumber
} from '@blisspointmedia/bpm-types/dist/formatUtils';

import { Button } from '@Components/Button';
import { DateBox } from '@Components/DateBox';
import { EntityKind, EntityLogo, EntitySize } from '@Components/EntityLogo';
import { useActiveOTTContext } from './active-ott-context';
import { CellData, Flight } from './active-ott-constants';
import { getPrettyWeek, makeCPM } from './active-ott-utils';
import { SpendBreakdown } from './SpendBreakdown';

import './DetailModal.scss';

const CANCELED_TIMESTAMP_FORMAT = 'MM/dd/yyy @ h:mm a';
const makeCanceledTimestamp = (iso: string) =>
  Dfns.format(CANCELED_TIMESTAMP_FORMAT)(Dfns.parseISO(iso));

interface DetailModalHeader {
  date: string;
  cid?: string;
  shortCode?: string;
  description?: string;
  platform?: string;
}

interface DetailModalProps {
  data: CellData;
  onClose: () => void;
}

export const DetailModal = ({ data, onClose }: DetailModalProps) => {
  const { flightMap, byNetwork } = useActiveOTTContext();

  const header = useMemo((): DetailModalHeader | null => {
    if (!data.flights?.length || !flightMap) {
      return null;
    }
    const firstFlight = flightMap[data.flights[0]];
    if (!firstFlight) {
      return null;
    }
    const headerInfo: DetailModalHeader = { date: getPrettyWeek(data.week) };
    if (byNetwork) {
      headerInfo.cid = firstFlight.key;
      return headerInfo;
    }
    const platformBits = (firstFlight.platform ?? '').split(' ');
    const platform =
      platformBits.length > 1 && platformBits[0] === 'Streaming'
        ? platformBits.slice(1).join(' ')
        : firstFlight.platform;
    return {
      ...headerInfo,
      shortCode: firstFlight.shortCode,
      description: firstFlight.description,
      platform
    };
  }, [data, flightMap, byNetwork]);

  const [flightProperties, flights] = useMemo((): [string[], Flight[]] => {
    if (!data.flights?.length || !flightMap) {
      return [[], []];
    }
    const properties: string[] = [];
    const flightList: Flight[] = [];
    for (const flightID of data.flights) {
      const flight = flightMap[flightID];
      if (flight.properties) {
        properties.push(...flight.properties);
      }
      flightList.push(flight);
    }
    return [[...new Set(properties)], flightList];
  }, [data, flightMap]);

  const [expandedFlightMap, setExpandedFlightMap] = useState<Record<string, boolean>>({});
  if (!header) {
    return null;
  }
  const logoId = byNetwork ? header.cid : header.shortCode;
  return (
    <Modal
      show
      onHide={onClose}
      dialogClassName='active-ott-detail-modal'
      data-testid='active-ott-detail-modal'
    >
      <Modal.Header closeButton>
        <Modal.Title className='modal-header'>
          {logoId && (
            <EntityLogo
              kind={byNetwork ? EntityKind.COMPANY : EntityKind.NETWORK}
              id={logoId}
              size={EntitySize.XL}
            />
          )}
          <div className='header-name'>
            {byNetwork ? (
              header.cid
            ) : (
              <>
                <div className='platform'>
                  {header.shortCode} {header.platform}
                </div>
                {header.description && <div className='description'>{header.description}</div>}
              </>
            )}
          </div>
          <div className='week'>{header.date}</div>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Row>
          <Col sm={12} md>
            <div className='data-section'>
              <div className='data-label'>Bookings</div>
              <div className='val-row'>
                <SpendBreakdown data={data} />
              </div>
            </div>
          </Col>
          {flightProperties.length > 0 && (
            <Col>
              <div className='data-section'>
                <div className='data-label'>Properties</div>
                <div className='val-row'>
                  <div className='unlabeled'>{flightProperties.join(', ')}</div>
                </div>
              </div>
            </Col>
          )}
        </Row>
        <div className='data-section'>
          <div className='data-label'>Flights</div>
          <div className='val-row'>
            <ListGroup className='flight-list'>
              {flights.map(flight => {
                const expanded = expandedFlightMap[flight.id];
                return (
                  <ListGroup.Item
                    key={flight.id}
                    action
                    onClick={() =>
                      setExpandedFlightMap(map => ({
                        ...map,
                        [flight.id]: !expanded
                      }))
                    }
                  >
                    <div className='main-flight-row'>
                      <div>
                        <div className='flight-ids'>
                          Flight #{flight.id}
                          <small>(Order #{flight.orderID})</small>
                        </div>
                        <DateBox start={flight.startDate} end={flight.endDate} />
                      </div>
                      <div>
                        <div className='labeled'>
                          <span>Spend:</span>
                          {formatMoneyAsInt(flight.spend)}
                        </div>
                        <div className='labeled'>
                          <span>CPM:</span>
                          {flight.cpm ? formatMoney(flight.cpm) : '$0'}
                        </div>
                        <div className='labeled'>
                          <span>Impressions:</span>
                          {formatNumber(flight.impressions)}
                        </div>
                      </div>
                    </div>
                    {expanded && (
                      <div className='expanded-section'>
                        {byNetwork && (
                          <>
                            <div className='labeled'>
                              <span>Platform:</span>
                              {flight.platform || 'No Description'}
                            </div>
                            <div className='labeled'>
                              <span>Description:</span>
                              {flight.description || 'No Description'}
                            </div>
                          </>
                        )}
                        {flight.properties?.length ? (
                          <div className='labeled'>
                            <span>Properties:</span>
                            {flight.properties.join(', ')}
                          </div>
                        ) : null}
                        {flight.canceled && (
                          <div className='labeled'>
                            <span>Canceled:</span>
                            {makeCanceledTimestamp(flight.canceled)}
                          </div>
                        )}
                        {flight.overrides && (
                          <div className='expanded-row'>
                            <div className='labeled'>
                              <span>
                                Overrides <small>(spend/cpm/imps)</small>:
                              </span>
                              {formatMoneyAsInt(flight.overrides.spend)} /{' '}
                              {formatMoney(
                                makeCPM(flight.overrides.spend, flight.overrides.impressions)
                              )}{' '}
                              / {formatNumber(flight.overrides.impressions)}
                            </div>
                          </div>
                        )}
                        {flight.derivedNetwork && (
                          <div className='labeled'>
                            <span>Derived Network:</span>
                            {flight.derivedNetwork}
                          </div>
                        )}
                        <div className='labeled'>
                          <span>PDF Text</span>
                        </div>
                        <div className='pdf-text'>
                          {flight.pdfText
                            ? flight.pdfText
                                .split('\n')
                                .map((text, i) => <p key={`${text}_${i}`}>{text || ' '}</p>)
                            : 'None'}
                        </div>
                      </div>
                    )}
                  </ListGroup.Item>
                );
              })}
            </ListGroup>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button onClick={onClose}>Close</Button>
      </Modal.Footer>
    </Modal>
  );
};
